/**
 * Disclosure control and reliability rules (brief 10, DIS-01 and
 * DIS-02), implemented behind the applyDisclosureRules and
 * applyComplementarySuppression stubs in engine.ts.
 *
 * Rule engine (brief 10.1): rules evaluate for every domain in a
 * fixed order; the first rule that fires decides the outcome — its
 * verdict becomes the domain's status and its identity is stored as
 * the reason. Later rules are not consulted for that domain, which
 * is what makes the stored reason well-defined. Suppressed domains
 * still contributed to model fitting; only their outputs are
 * withheld, by the export layer (decision S6: the engine stamps the
 * outcome; enforcement happens at export).
 *
 * Presets (brief 10.2, decision S6):
 * - 'simple' (and 'custom', which is the same rule set driven
 *   entirely by the caller's thresholds): suppress below the
 *   minimum respondent count; suppress when CV exceeds the
 *   suppression threshold; flag when CV exceeds the flag threshold;
 *   optionally suppress areas below a minimum sampled-PSU count
 *   (a minimum of 1 turns that rule off, per the brief).
 * - 'sae-standard' (the model-based preset "SAE Standard" of the
 *   Path to A guide, Part 4.2; thresholds registered as written,
 *   not tuned): applied per area to the model-based estimate
 *   pTilde, its reported interval [L, U], and its reported RMSE r
 *   (carried on the rule input as the model* fields, exactly as
 *   the shipped BackTransformedEstimate carries them). Publish
 *   when U - L <= 0.05; publish when U - L < 0.30 and the
 *   model-based CV r / pTilde <= 0.20; publish with flag when
 *   U - L < 0.30 and 0.20 < CV <= 0.30; suppress everything else,
 *   including an undefined interval. Areas with no respondents
 *   (n = 0) follow the same rule and always carry the
 *   'synthetic estimate' label, in addition to any flag. It does
 *   NOT read the configured simple/custom thresholds, the
 *   direct-estimate fields, or the proportion standard's fields.
 * - 'proportion-standard' (the standard preset of decision S6,
 *   modeled on published data presentation standards for
 *   proportions, Parker et al. 2017): suppress when the effective
 *   sample size is
 *   below 30; suppress when the absolute interval width is at least
 *   0.30; otherwise suppress when the absolute width exceeds 0.05
 *   and the relative interval width exceeds 130%; flag for review
 *   when an estimate based on zero events (or all events) has an
 *   interval at most 0.05 wide, and when the design degrees of
 *   freedom are below 8; plus the
 *   configured CV suppression and flag thresholds, which decision
 *   S6 fixes at 50% and 30%. The two width tiers are the standard's
 *   two separate width rules (an earlier build combined them into
 *   one conjunction; corrected under RB-1 after verification
 *   against the published text — docs/RB1_STANDARD_VERIFICATION.md
 *   records every threshold and boundary).
 *
 * How the standard maps onto this engine (RB-1 documentation;
 * Tier 2 Item 6 closed the two residuals recorded there):
 * - Widths: for a domain reported from its direct estimate, the
 *   width tests read the Korn-Graubard interval of the direct
 *   estimate — the interval the standard defines them on for
 *   complex surveys — carried on the rule input as
 *   standardIntervalWidth / standardRelativeIntervalWidth. The
 *   domain's reported interval is unchanged (brief 9.3: logit,
 *   Korn-Graubard when the proportion is 0 or 1); the standard
 *   governs the presentation test, not the reported interval
 *   (Addendum 12, Q6). When a pipeline cannot supply the
 *   Korn-Graubard width (an area-level domain whose file does not
 *   publish design degrees of freedom), the tests fall back to the
 *   reported interval's width. For a domain reported from its
 *   model-based estimate, the widths are those of the model
 *   interval.
 * - Zero events: an estimate with zero events, or all events,
 *   whose tested width is at most 0.05 is flagged for statistical
 *   review (ZERO_EVENT_REVIEW), as the standard specifies; it is
 *   not suppressed for being zero. At wider tested widths a
 *   zero-event estimate's relative width is unbounded, so the
 *   width tiers decide it, exactly as the standard's cascade
 *   reads (RB-1 verification, option analysis).
 * - Minimum size: the rule reads the domain's effective sample
 *   size neff = n / deff (smoothed design effect, clamped to
 *   [1.0, 5.0]), falling back to the unweighted n when neff is
 *   absent. The same neff feeds direct-reported and model-reported
 *   domains. Because neff <= n in this engine, the test also
 *   covers the standard's separate actual-sample-size minimum.
 * - Scope: Parker et al. set thresholds for survey proportions
 *   only. Applying the preset to model-based estimates is an
 *   extension of the standard, and the CV ladder (flag above 30%,
 *   suppress above 50%) is an addition from decision S6, not part
 *   of Parker et al.
 *
 * Rule order within each preset puts the small-count / small-sample
 * suppressions first (they are the privacy rules), then the width
 * and CV suppressions, then flags — so a domain failing both a
 * privacy rule and a reliability rule reports the privacy reason.
 */
/**
 * Structural constants of the proportion standard (brief 10.2),
 * verified against Parker et al. 2017 under RB-1
 * (docs/RB1_STANDARD_VERIFICATION.md). Boundary semantics are the
 * standard's: the large-width test is inclusive at 0.30, the
 * relative-width band is exclusive at both 0.05 and 130%.
 */
export const PROPORTION_STANDARD = {
    /** Suppress below this effective sample size. */
    minEffectiveSampleSize: 30,
    /** Suppress when the absolute interval width is at least this (tier 1). */
    absoluteWidthThreshold: 0.3,
    /**
     * Tier 2 applies only when the absolute interval width exceeds
     * this floor (an absolute width of exactly this value falls in
     * the standard's small-width branch and is never suppressed by
     * the relative-width test).
     */
    relativeWidthAbsoluteFloor: 0.05,
    /** Suppress in tier 2 when the relative interval width exceeds this. */
    relativeWidthThreshold: 1.3,
    /** Flag for review below this many design degrees of freedom. */
    minDegreesOfFreedom: 8,
};
/**
 * Structural constants of the model-based preset "SAE Standard"
 * (Path to A guide, Part 4.2). Boundary semantics are the
 * guide's: the narrow-width publish branch is inclusive at 0.05,
 * the wider branches require a width strictly below 0.30, the CV
 * publish branch is inclusive at 0.20, and the CV flag branch is
 * inclusive at 0.30.
 */
export const SAE_STANDARD = {
    /** Publish on interval width alone at or below this width. */
    narrowWidthThreshold: 0.05,
    /** The CV branches apply only strictly below this width. */
    widthCeiling: 0.3,
    /** Publish when the model-based CV is at most this. */
    cvPublishThreshold: 0.2,
    /** Flag when the model-based CV is above publish and at most this. */
    cvFlagThreshold: 0.3,
};
/** Label carried by every no-respondent area under 'sae-standard'. */
export const SYNTHETIC_ESTIMATE_LABEL = 'synthetic estimate';
/**
 * The 'sae-standard' decision (Path to A guide, Part 4.2), one
 * outcome per input. This preset does not fit the first-fires
 * rule list: its first two branches publish, so it is evaluated
 * directly, in the guide's branch order. Every outcome records
 * its branch's stable rule identifier, published outcomes
 * included. Guards run before any branch arithmetic so no
 * decision is ever a NaN comparison:
 * - an interval that is absent, non-finite, or inverted is
 *   undefined and suppresses (SAE_SUPPRESS_UNDEFINED_INTERVAL);
 * - a model estimate or reported RMSE from which no CV can be
 *   formed (pTilde absent, non-finite, or at most 0; r absent,
 *   non-finite, or negative) suppresses
 *   (SAE_SUPPRESS_INVALID_CV) — the reading implemented here is
 *   that this guard precedes even the narrow-width branch,
 *   because a CV that cannot be formed cannot be shown to
 *   satisfy any branch that the export records for the area.
 */
function saeStandardOutcome(input) {
    const labels = input.n === 0 ? [SYNTHETIC_ESTIMATE_LABEL] : undefined;
    const base = {
        domain: input.domain,
        ...(labels !== undefined ? { labels } : {}),
    };
    const { modelEstimate: p, modelLower: lower, modelUpper: upper, modelRmse: r } = input;
    if (lower === undefined ||
        upper === undefined ||
        !Number.isFinite(lower) ||
        !Number.isFinite(upper) ||
        upper < lower) {
        return {
            ...base,
            status: 'suppressed',
            ruleId: 'SAE_SUPPRESS_UNDEFINED_INTERVAL',
            reason: 'The model-based interval is undefined, so the estimate is ' +
                'withheld under the SAE Standard preset.',
        };
    }
    if (p === undefined ||
        !Number.isFinite(p) ||
        p <= 0 ||
        r === undefined ||
        !Number.isFinite(r) ||
        r < 0) {
        return {
            ...base,
            status: 'suppressed',
            ruleId: 'SAE_SUPPRESS_INVALID_CV',
            reason: 'The model-based coefficient of variation cannot be formed ' +
                'from the reported estimate and RMSE, so the estimate is ' +
                'withheld under the SAE Standard preset.',
        };
    }
    const width = upper - lower;
    const cv = r / p;
    if (width <= SAE_STANDARD.narrowWidthThreshold) {
        return { ...base, status: 'publish', ruleId: 'SAE_PUBLISH_NARROW' };
    }
    if (width < SAE_STANDARD.widthCeiling && cv <= SAE_STANDARD.cvPublishThreshold) {
        return { ...base, status: 'publish', ruleId: 'SAE_PUBLISH_CV' };
    }
    if (width < SAE_STANDARD.widthCeiling && cv <= SAE_STANDARD.cvFlagThreshold) {
        return {
            ...base,
            status: 'flagged',
            ruleId: 'SAE_FLAG_CV',
            reason: `The model-based coefficient of variation is above ` +
                `${Math.round(SAE_STANDARD.cvPublishThreshold * 100)}% but at most ` +
                `${Math.round(SAE_STANDARD.cvFlagThreshold * 100)}%, so the estimate ` +
                'is flagged under the SAE Standard preset.',
        };
    }
    return {
        ...base,
        status: 'suppressed',
        ruleId: 'SAE_SUPPRESS_WIDTH_OR_CV',
        reason: 'The model-based interval is at least 0.30 wide, or its ' +
            'coefficient of variation exceeds 30%, so the estimate is ' +
            'withheld under the SAE Standard preset.',
    };
}
/**
 * The width the proportion standard's tests read (Tier 2 Item 6):
 * the Korn-Graubard width of the direct estimate when the pipeline
 * supplied one, else the reported interval's width.
 */
function standardWidthOf(d) {
    return d.standardIntervalWidth ?? d.intervalWidth;
}
function standardRelativeWidthOf(d) {
    return d.standardRelativeIntervalWidth ?? d.relativeIntervalWidth;
}
function rulesFor(config) {
    const psuRule = {
        ruleId: 'MIN_PSUS',
        status: 'suppressed',
        reason: `Fewer than ${config.minPsusPerArea} sampled PSUs in this area, so ` +
            'the estimate is withheld under the minimum-PSU rule.',
        fires: (d) => config.minPsusPerArea > 1 && d.psuCount < config.minPsusPerArea,
    };
    const cvSuppress = {
        ruleId: 'CV_SUPPRESS',
        status: 'suppressed',
        reason: `The coefficient of variation exceeds ${Math.round(config.cvSuppress * 100)}%, ` +
            'so the estimate is withheld as too unreliable to publish.',
        fires: (d) => d.cv > config.cvSuppress,
    };
    const cvFlag = {
        ruleId: 'CV_FLAG',
        status: 'flagged',
        reason: `The coefficient of variation exceeds ${Math.round(config.cvFlag * 100)}%, ` +
            'so the estimate is flagged as unstable.',
        fires: (d) => d.cv > config.cvFlag,
    };
    if (config.preset === 'proportion-standard') {
        return [
            psuRule,
            {
                ruleId: 'EFFN_SUPPRESS',
                status: 'suppressed',
                reason: `The effective sample size is below ${PROPORTION_STANDARD.minEffectiveSampleSize}, ` +
                    'so the estimate is withheld under the proportion presentation standard.',
                fires: (d) => (d.effectiveSampleSize ?? d.n) <
                    PROPORTION_STANDARD.minEffectiveSampleSize,
            },
            {
                // Width tier 1 (Parker et al. 2017, pp. 2 and 4): an
                // absolute interval width of at least 0.30 suppresses on
                // its own, whatever the relative width.
                ruleId: 'CI_WIDTH_SUPPRESS',
                status: 'suppressed',
                reason: 'The confidence interval is at least 0.30 wide, so the ' +
                    'estimate is withheld under the proportion presentation ' +
                    'standard.',
                fires: (d) => standardWidthOf(d) >= PROPORTION_STANDARD.absoluteWidthThreshold,
            },
            {
                // Width tier 2 (Parker et al. 2017, pp. 2 and 4): within
                // the (0.05, 0.30) absolute-width band, a relative width of
                // more than 130% suppresses. Both bounds are exclusive:
                // exactly 0.05 is the standard's small-width branch, and a
                // relative width of exactly 130% can be presented.
                ruleId: 'CI_REL_WIDTH_SUPPRESS',
                status: 'suppressed',
                reason: 'The confidence interval is more than 0.05 wide and more ' +
                    'than 130% of the estimate, so the estimate is withheld ' +
                    'under the proportion presentation standard.',
                fires: (d) => standardWidthOf(d) > PROPORTION_STANDARD.relativeWidthAbsoluteFloor &&
                    standardWidthOf(d) < PROPORTION_STANDARD.absoluteWidthThreshold &&
                    standardRelativeWidthOf(d) > PROPORTION_STANDARD.relativeWidthThreshold,
            },
            cvSuppress,
            {
                // Zero-event review flag (Parker et al. 2017, p. 4; Ward
                // et al. 2019, section 2.2): an estimate based on zero
                // events, or on all events (the complement case), whose
                // interval is at most 0.05 wide is flagged for statistical
                // review — presented if every other criterion is met, but
                // reviewed. The tested width is the standard width above,
                // so on the complex-survey path it is the Korn-Graubard
                // width, as the standard specifies. Wider zero-event
                // estimates never reach this rule: their relative width is
                // unbounded, so width tier 2 has already decided them.
                ruleId: 'ZERO_EVENT_REVIEW',
                status: 'flagged',
                reason: 'The estimate is based on zero events (or all events) ' +
                    'with a narrow confidence interval, so it is flagged for ' +
                    'statistical review under the proportion presentation ' +
                    'standard.',
                fires: (d) => d.eventCount !== undefined &&
                    d.n > 0 &&
                    (d.eventCount === 0 || d.eventCount === d.n) &&
                    standardWidthOf(d) > 0 &&
                    standardWidthOf(d) <=
                        PROPORTION_STANDARD.relativeWidthAbsoluteFloor,
            },
            {
                ruleId: 'DF_REVIEW',
                status: 'flagged',
                reason: `Fewer than ${PROPORTION_STANDARD.minDegreesOfFreedom} design ` +
                    'degrees of freedom, so the estimate is flagged for review ' +
                    'under the proportion presentation standard.',
                fires: (d) => d.degreesOfFreedom !== undefined &&
                    d.degreesOfFreedom < PROPORTION_STANDARD.minDegreesOfFreedom,
            },
            cvFlag,
        ];
    }
    // 'simple' and 'custom'.
    return [
        psuRule,
        {
            ruleId: 'SUPPRESS_N',
            status: 'suppressed',
            reason: `Fewer than ${config.minRespondents} respondents answered in this ` +
                'group, so the estimate is withheld to protect privacy and ' +
                'because it would be unreliable.',
            fires: (d) => d.n < config.minRespondents,
        },
        cvSuppress,
        cvFlag,
    ];
}
/**
 * Evaluate the disclosure rules for every domain (brief 10.1). The
 * outcome array is parallel to the input array.
 */
export function applyDisclosureRulesImpl(inputs, config) {
    if (config.preset === 'sae-standard') {
        return inputs.map(saeStandardOutcome);
    }
    const rules = rulesFor(config);
    return inputs.map((input) => {
        for (const rule of rules) {
            if (rule.fires(input)) {
                return {
                    domain: input.domain,
                    status: rule.status,
                    ruleId: rule.ruleId,
                    reason: rule.reason,
                };
            }
        }
        return { domain: input.domain, status: 'publish' };
    });
}
function domainKeyOf(domain) {
    return `${domain.cellId}${domain.areaId}`;
}
/**
 * Complementary suppression (brief 10.3, DIS-02). Within each
 * (indicator, area, subgroup variable) family, the level counts sum
 * to the area's overall count, so when exactly one level's count is
 * suppressed it could be recovered by subtraction from the published
 * total and the other levels. The rule therefore also suppresses
 * the next-smallest level's count (ties break toward the earlier
 * level in input order). The same withholding covers the level's
 * direct-estimate percentages, which are complements within the
 * family. Families with zero or at least two suppressed levels are
 * left alone, as are families of a single level (nothing within the
 * family can protect them) and overall domains, which are the
 * totals the subtraction would use, not levels of a family.
 *
 * Families are disjoint in their domains (a domain belongs to
 * exactly one subgroup variable), so a single pass suffices: after
 * the pass every family has zero or at least two suppressed levels.
 * Outcomes upgraded here carry ruleId 'COMPLEMENTARY'.
 */
export function applyComplementarySuppressionImpl(outcomes, inputs) {
    const byKey = new Map();
    for (const o of outcomes)
        byKey.set(domainKeyOf(o.domain), o);
    const families = new Map();
    for (const input of inputs) {
        const d = input.domain;
        if (d.subgroupVariable === undefined)
            continue;
        const key = `${d.indicatorId}${d.areaId}${d.subgroupVariable}`;
        let fam = families.get(key);
        if (!fam) {
            fam = [];
            families.set(key, fam);
        }
        fam.push(input);
    }
    const upgraded = new Map();
    for (const fam of families.values()) {
        if (fam.length < 2)
            continue;
        const suppressed = fam.filter((input) => byKey.get(domainKeyOf(input.domain))?.status === 'suppressed');
        if (suppressed.length !== 1)
            continue;
        let target;
        for (const input of fam) {
            if (input === suppressed[0])
                continue;
            if (target === undefined || input.n < target.n)
                target = input;
        }
        if (target === undefined)
            continue;
        const key = domainKeyOf(target.domain);
        const original = byKey.get(key);
        if (original === undefined)
            continue;
        upgraded.set(key, {
            domain: original.domain,
            status: 'suppressed',
            ruleId: 'COMPLEMENTARY',
            ...(original.labels !== undefined ? { labels: original.labels } : {}),
            reason: 'Suppressed so that another withheld value in the same group ' +
                'cannot be recovered by subtraction from the published total ' +
                '(complementary suppression, brief 10.3).',
        });
    }
    return outcomes.map((o) => upgraded.get(domainKeyOf(o.domain)) ?? o);
}
//# sourceMappingURL=disclosure.js.map